import { BaseNodeCommand, NodeChangeRecord } from './base/BaseNodeCommand';
import { useDiagramStore } from '../../stores/diagramStore';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import { expandNodeIdsWithGroupMembers } from '../../lib/diagram/grouping';

type Mode = 'front' | 'forward' | 'backward' | 'back';

export class AdjustZIndexCommand extends BaseNodeCommand {
  private ids: string[];
  private mode: Mode;

  constructor(ids: string[], mode: Mode) {
    super('Adjust Z-Index', `Set z-index ${mode} for ${ids.length} nodes`);
    this.ids = ids;
    this.mode = mode;
  }

  protected doExecute(): NodeChangeRecord[] {
    const records: NodeChangeRecord[] = [];

    const storeNodes = useDiagramStore.getState().nodes as any[];
    if (storeNodes.length === 0 || this.ids.length === 0) return records;

    const groupIds = expandNodeIdsWithGroupMembers(this.ids, storeNodes);
    const groupNodes = storeNodes.filter(n => groupIds.has(n.id));
    if (groupNodes.length === 0) return records;

    const getW = (n: any) => (n.w ?? n.width ?? 60) as number;
    const getH = (n: any) => (n.h ?? n.height ?? 60) as number;
    const rectOf = (n: any) => ({ x: n.x, y: n.y, w: getW(n), h: getH(n) });
    const overlaps = (a: {x:number;y:number;w:number;h:number}, b: {x:number;y:number;w:number;h:number}) => {
      return !(a.x + a.w <= b.x || b.x + b.w <= a.x || a.y + a.h <= b.y || b.y + b.h <= a.y);
    };

    const zOf = (n: any) => Number(n?.zIndex ?? 0);
    const maxZ = storeNodes.reduce((m, n) => Math.max(m, zOf(n)), -Infinity);
    const minZ = storeNodes.reduce((m, n) => Math.min(m, zOf(n)), Infinity);
    const groupMaxZ = groupNodes.reduce((m, n) => Math.max(m, zOf(n)), -Infinity);
    const groupMinZ = groupNodes.reduce((m, n) => Math.min(m, zOf(n)), Infinity);
    const groupRect = groupNodes.reduce((acc, node) => {
      const rect = rectOf(node);
      if (!acc) return { ...rect };
      const minX = Math.min(acc.x, rect.x);
      const minY = Math.min(acc.y, rect.y);
      const maxX = Math.max(acc.x + acc.w, rect.x + rect.w);
      const maxY = Math.max(acc.y + acc.h, rect.y + rect.h);
      return { x: minX, y: minY, w: maxX - minX, h: maxY - minY };
    }, null as any);
    const overlappingNodes = storeNodes.filter(n => !groupIds.has(n.id) && overlaps(rectOf(n), groupRect));

    const mm = getNodeMutationManager();
    const orderedGroup = groupNodes.slice().sort((a, b) => zOf(a) - zOf(b));

    const setZDelta = (delta: number) => {
      groupNodes.forEach((node) => {
        const nextZ = zOf(node) + delta;
        const rec = mm.applyNodeChange(node.id, { zIndex: nextZ } as any);
        records.push(rec);
      });
    };
    const shiftGroupWithinSlot = (targetSlot: number, zList: number[], eps = 0.0001) => {
      const lower = targetSlot - 1 >= 0 ? zList[targetSlot - 1] : (Number.isFinite(minZ) ? minZ : 0) - 1;
      const upper = targetSlot < zList.length ? zList[targetSlot] : (Number.isFinite(maxZ) ? maxZ : 0) + 1;
      const span = Math.max(0, groupMaxZ - groupMinZ);
      const available = upper - lower;
      const scale = span > 0 && available > 2 * eps ? Math.min(1, (available - 2 * eps) / span) : 0;
      orderedGroup.forEach((node, idx) => {
        const offset = zOf(node) - groupMinZ;
        const nextZ = span > 0 && available > 2 * eps
          ? lower + eps + offset * scale
          : lower + eps + idx * eps;
        const rec = mm.applyNodeChange(node.id, { zIndex: nextZ } as any);
        records.push(rec);
      });
    };

    switch (this.mode) {
      case 'front': {
        const delta = ((Number.isFinite(maxZ) ? maxZ : 0) + 1) - groupMinZ;
        setZDelta(delta);
        break;
      }
      case 'back': {
        const delta = ((Number.isFinite(minZ) ? minZ : 0) - 1) - groupMaxZ;
        setZDelta(delta);
        break;
      }
      case 'forward': {
        if (overlappingNodes.length === 0) return records;
        const candidateNodes = overlappingNodes.slice().sort((a, b) => zOf(a) - zOf(b));
        const zList = candidateNodes.map(zOf);
        const currentSlot = candidateNodes.filter(n => zOf(n) < groupMinZ).length;
        const targetSlot = Math.min(currentSlot + 1, zList.length);
        if (targetSlot === currentSlot) return records;
        shiftGroupWithinSlot(targetSlot, zList);
        break;
      }
      case 'backward': {
        if (overlappingNodes.length === 0) return records;
        const candidateNodes = overlappingNodes.slice().sort((a, b) => zOf(a) - zOf(b));
        const zList = candidateNodes.map(zOf);
        const currentSlot = candidateNodes.filter(n => zOf(n) < groupMinZ).length;
        const targetSlot = Math.max(currentSlot - 1, 0);
        if (targetSlot === currentSlot) return records;
        shiftGroupWithinSlot(targetSlot, zList);
        break;
      }
    }

    return records;
  }
}
